// 文献通知服务 - 适配实验室管理系统的API调用方式
const API_BASE = '/api';

// 辅助函数：获取token
const getToken = () => {
  return localStorage.getItem('token');
};

// 辅助函数：API请求
const apiRequest = async (url: string, options: RequestInit = {}) => {
  const token = getToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> || {}),
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE}${url}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    if (response.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/';
      throw new Error('未授权，请重新登录');
    }
    const error = await response.json().catch(() => ({ message: '请求失败' }));
    throw new Error(error.message || '请求失败');
  }

  return response.json();
};

export interface LiteratureNotification {
  _id: string;
  type:
    | 'shared_folder_invite'
    | 'shared_folder_accepted'
    | 'shared_folder_rejected'
    | 'shared_experiment_notebook_invite'
    | 'shared_experiment_notebook_accepted'
    | 'shared_experiment_notebook_rejected';
  recipient: string;
  sender: {
    _id: string;
    username: string;
    email: string;
  };
  folder?: {
    _id: string;
    name: string;
    description?: string;
  };
  experimentNotebookFolder?: {
    _id: string;
    name: string;
    description?: string;
  };
  title: string;
  content: string;
  status: 'pending' | 'accepted' | 'rejected' | 'read';
  readAt?: string;
  createdAt: string;
  updatedAt: string;
}

export const literatureNotificationService = {
  // 获取通知列表
  getList: async (params?: { status?: string; type?: string; limit?: number }): Promise<LiteratureNotification[]> => {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.keys(params).forEach(key => {
        const value = params[key as keyof typeof params];
        if (value !== undefined && value !== null) {
          queryParams.append(key, String(value));
        }
      });
    }
    const response = await apiRequest(`/literature-notifications?${queryParams.toString()}`);
    return response.data.notifications;
  },

  // 获取未读通知数量
  getUnreadCount: async (): Promise<number> => {
    const response = await apiRequest('/literature-notifications/unread-count');
    return response.data.count;
  },

  // 标记通知为已读
  markAsRead: async (id: string): Promise<LiteratureNotification> => {
    const response = await apiRequest(`/literature-notifications/${id}/read`, { method: 'PUT' });
    return response.data.notification;
  },

  // 批量标记为已读
  markAllAsRead: async (): Promise<void> => {
    await apiRequest('/literature-notifications/read-all', { method: 'PUT' });
  },

  // 接受共享文件夹邀请
  acceptInvite: async (id: string) => {
    const response = await apiRequest(`/literature-notifications/${id}/accept`, { method: 'POST' });
    return response.data;
  },

  // 拒绝共享文件夹邀请
  rejectInvite: async (id: string): Promise<void> => {
    await apiRequest(`/literature-notifications/${id}/reject`, { method: 'POST' });
  },

  // 删除通知
  delete: async (id: string): Promise<void> => {
    await apiRequest(`/literature-notifications/${id}`, { method: 'DELETE' });
  }
};

// 导出为 notificationService 以兼容现有代码
export const notificationService = literatureNotificationService;
